import { ICON_TYPE, SHOW_TASK_BY_ORDER , SHOW_TASK_GROUPING_TYPE , TASK_LIST_TYPE , SHOW_COMPLETED_TASK_BY_DATE } from '../../global/constants';
import { captureException } from '@sentry/react';
import { NewTaskFilterInterface } from '../../interfaces/TaskInterface';
import { defaultDisplayFilters } from '../common';
import { DEFAULT_VIEW_FILTERS, RawView, ViewFilters, Icon } from '../interfaces/view';
import { rootStore } from '../rootStore';
import { makeAutoObservable } from 'mobx';

export class ViewModel {
  // Common
  id: string;
  name: string;
  description: string;
  icon: Icon = {
    type: ICON_TYPE.ICON,
    reference: '',
    color: 1
  };
  color: number = 1;
  userIds: string[] = [];
  filters: ViewFilters;

  //Default fields
  createdById: string;
  createdOn: Date;
  updatedById: string;
  updatedOn: Date;

  //Display filters
  displayFilters: NewTaskFilterInterface = defaultDisplayFilters;
  isReadOnly: boolean;

  constructor(rawView: RawView) {
    makeAutoObservable(this);

    // Common
    this.id = rawView.id;
    this.name = rawView.name;
    this.description = rawView.description;
    this.icon = rawView.icon;
    this.color = rawView.color;
    this.filters = rawView.filters;
    this.userIds = rawView.userIds;

    //Default fields
    this.createdById = rawView.createdById;
    this.createdOn = rawView.createdOn;
    this.updatedById = rawView.updatedById;
    this.updatedOn = rawView.updatedOn;

    //Display filters
    this.displayFilters = {
      showSubTask: false,
      showCompletedTaskByFilter: SHOW_COMPLETED_TASK_BY_DATE.LAST_MONTH,
      showRecentlyCompletedTask: true,
      showEmptyGroup: true,
      taskViewType: TASK_LIST_TYPE.TASKS,
      groupingStatus: SHOW_TASK_GROUPING_TYPE.GROUPING_BY_STATUS,
      orderByStatus: SHOW_TASK_BY_ORDER.CUSTOM
    };
    this.isReadOnly = rawView.isReadOnly;
  }

  get users() {
    return rootStore.workspaceStore.currentWorkspace?.users.filter((user) => this.userIds.includes(user.id)) || [];
  }

  clearAllFilters() {
    this.filters = DEFAULT_VIEW_FILTERS;
  }

  setDisplayFilters(displayFilters: NewTaskFilterInterface) {
    this.displayFilters = displayFilters;
  }

  updateTaskFilterType<K extends keyof NewTaskFilterInterface>(propsName: K, value: NewTaskFilterInterface[K]) {
    this.displayFilters[propsName] = value;
  }

  /**
   * Applies view preference data (display filters) supplied by the caller onto this view.
   *
   * The preference data is taken directly from the Firestore snapshot (listener) or from a
   * single bulk read (see ViewStore.updateViewPreferenceFromFirebase), so this no longer
   * issues a per-view getDoc() read. That breaks the listener-to-read cascade where one
   * preference change triggered a Firestore read for every view.
   *
   * @param viewPreference The view's preference document data (subset of display filters).
   */
  updateViewPreferenceFromFirebase(viewPreference: Partial<NewTaskFilterInterface>) {
    try {
      if (!viewPreference) return;
      const displayFilters = {
        ...this.displayFilters,
        ...viewPreference
      };
      this.setDisplayFilters(displayFilters);
    } catch (err) {
      captureException(err);
    }
  }
}

export const createObservableView = (rawView: RawView) => {
  return new ViewModel(rawView);
};
